import styled from 'styled-components';

import useInvalidatedPatientQueries from 'components/patient/useInvalidatedPatientQueries';
import Button from 'components/shared/Button';
import StandardModal, { Actions, ColorThemeName, PatientName } from 'components/shared/StandardModal';
import { useProfile } from 'context/profile';
import useMutateWithToast from 'hooks/useMutateWithToast';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewStatus } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { updateReview } from 'services/api/reviews';
import colors from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

export type CancelReferralModalType = {
  setShow: (b: boolean) => void;
  locationEpisode: LocationEpisode;
  referral: Referral;
};

export default function CancelReferralModal(props: CancelReferralModalType) {
  const { setShow, referral, locationEpisode } = props;
  const referralId = referral.id;

  const { profile } = useProfile();
  const invalidatePatientQueries = useInvalidatedPatientQueries();

  const patientName = locationEpisode.patient.name;
  const referralDisplayStatus = referral.statusLabel;

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: updateReview,
    onSuccess: () => {
      invalidatePatientQueries({
        locationEpisode: { id: locationEpisode.id },
        referrals: true,
        referral: { id: referralId },
      });

      setShow(false);
    },
  });

  const submit = () => {
    mutate({
      id: referralId,
      status: ReviewStatus.CANCELED,
      reviewedById: profile.id,
    });
  };

  return (
    <StandardModal
      title={`Cancel ${referralDisplayStatus} Referral?`}
      colorTheme={ColorThemeName.PrimaryRed}
      onCancel={() => {
        setShow(false);
      }}>
      <PatientName style={{ color: colors.black }}>Patient: {patientName}</PatientName>
      <FormContainer>
        <BodySmallBold style={{ lineHeight: '22px' }}>
          Are you sure you wish to cancel this {referralDisplayStatus.toLowerCase()} referral?
        </BodySmallBold>
        <BodySmall style={{ lineHeight: '22px' }}>
          A history of the referral can still be accessed after canceling.
        </BodySmall>
      </FormContainer>
      <Actions>
        <Button
          onClick={() => {
            setShow(false);
          }}
          variant='ghost'>
          Go Back
        </Button>
        <Button onClick={submit} variant='danger' loading={isPending}>
          Cancel Referral
        </Button>
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  color: ${colors.black};
  font-size: 14px;
  letter-spacing: 0.2px;
  margin-bottom: 64px;
`;
